import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import LeadMagnetForm from "../components/LeadMagnetForm";

const SECTIONS = [
  {
    n: "01", title: "Highest-absorption routes first",
    items: [
      "Filter your drinking water (ingestion is the highest-uptake route)",
      "Stop heating food in plastic, use glass or stainless for reheating",
      "Improve ventilation: open windows daily, run kitchen/bath fans",
    ],
  },
  {
    n: "02", title: "Daily, leave-on contact",
    items: [
      "Audit leave-on personal care (lotion, deodorant, sunscreen, makeup)",
      "Consolidate redundant products to cut cumulative exposure",
      "Treat 'fragrance' as a question, not a reassurance",
    ],
  },
  {
    n: "03", title: "Home baseline",
    items: [
      "Let new furniture, mattresses, and rugs off-gas in ventilated space",
      "Favor solid wood/metal/glass over composites where feasible",
      "Reserve the strongest cleaners for when they're actually needed",
    ],
  },
  {
    n: "04", title: "Decide like an engineer",
    items: [
      "Prioritize by impact, not by whatever is loudest online",
      "Aim for 'good enough,' not perfect, to protect your capacity",
      "Revisit decisions as life stage and evidence change",
    ],
  },
];

export default function ChecklistPage() {
  return (
    <div data-testid="checklist-page">
      <Seo title="Low-Tox Quick-Start Checklist" path="/resources/quick-start-checklist"
        description="A free, prioritized checklist to start reducing cumulative exposure, sequenced by impact, the engineering way." />
      <section className="grid-bg grain border-b border-border">
        <div className="mx-auto max-w-[760px] px-5 sm:px-8 py-20">
          <p className="mono-label">Free resource</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight">Low-Tox Quick-Start Checklist</h1>
          <p className="mt-5 text-base md:text-lg text-foreground/65 leading-relaxed">
            Twelve high-leverage moves, sequenced by impact. Start at the top, each step lowers cumulative load
            without demanding perfection. Read it below, or get the printable version delivered to your inbox.
          </p>
          <div className="mt-8 max-w-md">
            <LeadMagnetForm />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[760px] px-5 sm:px-8 py-16 space-y-10">
        {SECTIONS.map((s) => (
          <div key={s.n}>
            <div className="flex items-center gap-3">
              <span className="font-mono text-2xl text-primary/80">{s.n}</span>
              <h2 className="font-display text-2xl font-medium tracking-tight">{s.title}</h2>
            </div>
            <ul className="mt-4 space-y-2.5">
              {s.items.map((it, i) => (
                <li key={i} className="flex gap-3 text-base text-foreground/80 leading-relaxed border border-border bg-surface px-4 py-3">
                  <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />{it}
                </li>
              ))}
            </ul>
          </div>
        ))}
        <div className="border border-border bg-surface/40 p-8">
          <h3 className="font-display text-xl">Want the full system?</h3>
          <p className="mt-2 text-sm text-foreground/60">Go deeper with the Greenpapers, or get the complete room-by-room plan in The Low-Tox Home Blueprint.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/greenpapers" className="inline-flex items-center gap-2 border border-border px-5 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors">Read Greenpapers</Link>
            <Link to="/products/low-tox-home-blueprint" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary-hover transition-colors">View the Blueprint <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
